/**
 * Schema entities definition and color schemes
 */

import { SchemaEntity, ColorScheme, NetworkColors } from './types';

export const schemaEntities: SchemaEntity[] = [
  {
    name: 'companies',
    type: 'table',
    fields: [
      { name: 'id', type: 'serial', isPrimaryKey: true, isRequired: true },
      { name: 'name', type: 'text', isRequired: true },
      { name: 'logoPath', type: 'text', isRequired: false },
      { name: 'logoText', type: 'text', isRequired: false }
    ],
    relationships: [
      { target: 'activities', type: 'one-to-many' }
    ]
  },
  {
    name: 'activities',
    type: 'table',
    fields: [
      { name: 'id', type: 'serial', isPrimaryKey: true, isRequired: true },
      { name: 'title', type: 'text', isRequired: true },
      { name: 'type', type: 'text', isRequired: true },
      { name: 'startDate', type: 'date', isRequired: true },
      { name: 'endDate', type: 'date', isRequired: false },
      { name: 'companyId', type: 'integer', isForeignKey: true, isRequired: false, references: 'companies.id' },
      { name: 'tempAgencyId', type: 'integer', isForeignKey: true, isRequired: false, references: 'companies.id' },
      { name: 'department', type: 'text', isRequired: false },
      { name: 'position', type: 'text', isRequired: false },
      { name: 'description', type: 'text', isRequired: false },
      { name: 'gpa', type: 'text', isRequired: false },
      { name: 'degree', type: 'text', isRequired: false }
    ],
    relationships: [
      { target: 'project_activities', type: 'one-to-many' },
      { target: 'certificate_activities', type: 'one-to-many' }
    ]
  },
  {
    name: 'projects',
    type: 'table',
    fields: [
      { name: 'id', type: 'serial', isPrimaryKey: true, isRequired: true },
      { name: 'title', type: 'text', isRequired: true },
      { name: 'startDate', type: 'date', isRequired: true },
      { name: 'endDate', type: 'date', isRequired: false },
      { name: 'description', type: 'text', isRequired: false },
      { name: 'goal', type: 'text', isRequired: false },
      { name: 'details', type: 'text', isRequired: false },
      { name: 'metrics', type: 'text[]', isRequired: false },
      { name: 'role', type: 'text', isRequired: false },
      { name: 'teamSize', type: 'text', isRequired: false },
      { name: 'git', type: 'text', isRequired: false },
      { name: 'link', type: 'text', isRequired: false },
      { name: 'status', type: 'text', isRequired: false },
      { name: 'isProfessional', type: 'boolean', isRequired: true }
    ],
    relationships: [
      { target: 'project_activities', type: 'one-to-many' },
      { target: 'project_skills', type: 'one-to-many' }
    ]
  },
  {
    name: 'certificates',
    type: 'table',
    fields: [
      { name: 'id', type: 'serial', isPrimaryKey: true, isRequired: true },
      { name: 'title', type: 'text', isRequired: true },
      { name: 'issuingOrganization', type: 'text', isRequired: true },
      { name: 'issueDate', type: 'date', isRequired: true },
      { name: 'expiryDate', type: 'date', isRequired: false },
      { name: 'credentialUrl', type: 'text', isRequired: false },
      { name: 'verificationUrl', type: 'text', isRequired: false },
      { name: 'pdfPath', type: 'text', isRequired: false },
      { name: 'pdfUrl', type: 'text', isRequired: false },
      { name: 'logoPath', type: 'text', isRequired: false },
      { name: 'description', type: 'text', isRequired: false },
      { name: 'skills', type: 'text[]', isRequired: false },
      { name: 'link', type: 'text', isRequired: false },
      { name: 'displayOrder', type: 'integer', isRequired: false },
      { name: 'isVisible', type: 'boolean', isRequired: true }
    ],
    relationships: [
      { target: 'certificate_activities', type: 'one-to-many' }
    ]
  },
  {
    name: 'skills',
    type: 'table',
    fields: [
      { name: 'name', type: 'text', isPrimaryKey: true, isRequired: true },
      { name: 'level', type: 'integer', isRequired: true },
      { name: 'category', type: 'text', isRequired: true }
    ],
    relationships: [
      { target: 'project_skills', type: 'one-to-many' }
    ]
  },
  {
    name: 'project_activities',
    type: 'junction',
    fields: [
      { name: 'projectId', type: 'integer', isForeignKey: true, isRequired: true, references: 'projects.id' },
      { name: 'activityId', type: 'integer', isForeignKey: true, isRequired: true, references: 'activities.id' }
    ],
    relationships: []
  },
  {
    name: 'project_skills',
    type: 'junction',
    fields: [
      { name: 'projectId', type: 'integer', isForeignKey: true, isRequired: true, references: 'projects.id' },
      { name: 'skillName', type: 'text', isForeignKey: true, isRequired: true, references: 'skills.name' }
    ],
    relationships: []
  },
  {
    name: 'certificate_activities',
    type: 'junction',
    fields: [
      { name: 'certificateId', type: 'integer', isForeignKey: true, isRequired: true, references: 'certificates.id' },
      { name: 'activityId', type: 'integer', isForeignKey: true, isRequired: true, references: 'activities.id' }
    ],
    relationships: []
  }
];

export const schemaColors: ColorScheme = {
  table: { bg: '#1E293B', border: '#0F172A', text: '#FFFFFF' },
  junction: { bg: '#065F46', border: '#064E3B', text: '#FFFFFF' },
  enum: { bg: '#581C87', border: '#4C1D95', text: '#FFFFFF' },
  field: { bg: '#334155', border: '#475569', text: '#E2E8F0' },
  pk: { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' },
  fk: { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' }
};

export const networkColors: NetworkColors = {
  company: { main: '#3B82F6', light: '#93C5FD', dark: '#1D4ED8' },
  activity: { main: '#10B981', light: '#6EE7B7', dark: '#047857' },
  project: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706' },
  certificate: { main: '#8B5CF6', light: '#C4B5FD', dark: '#7C3AED' },
  skill: { main: '#EF4444', light: '#FCA5A5', dark: '#DC2626' },
  default: { main: '#6B7280', light: '#D1D5DB', dark: '#374151' }
};
